import { IWorkspaceService } from './IWorkspaceService'
import { IWorkspaceRepository } from '../repositories/IWorkspaceRepository'
import { WorkspaceSettings, PlatformBranding } from '../models/Workspace'

/**
 * After a thrown fetch the next ask waits this long before fetching again, so a broken backend
 * costs one request per interval however often the controllers remount.
 */
const FALLBACK_RETRY_COOLDOWN_MS = 30_000

/** A `fresh` read came back empty, so the caller has no current copy to work from. */
export class FreshSettingsUnavailableError extends Error {
  constructor() {
    super('Current workspace settings could not be loaded')
    this.name = 'FreshSettingsUnavailableError'
  }
}

export class WorkspaceService implements IWorkspaceService {
  private cachedSettings: WorkspaceSettings | null = null
  private settingsLoaded = false
  /**
   * `cachedSettings` is the built-in defaults, put there by a thrown fetch so the app can start.
   * They are a stand-in, not a result: `ensureSettingsLoaded()` keeps trying for the real ones.
   */
  private usedFallback = false
  private retryFallbackAfter = 0
  private loadInFlight: Promise<void> | null = null
  /**
   * Counts fetches and saves. Responses can arrive out of order (a forced `loadSettings()`
   * overlapping `ensureSettingsLoaded()`, or a fetch started before a save), and only the latest
   * one may be applied.
   */
  private fetchSeq = 0

  constructor(private workspaceRepository: IWorkspaceRepository) {}

  async getSettings(): Promise<WorkspaceSettings | null> {
    if (!this.settingsLoaded) {
      await this.ensureSettingsLoaded()
    }
    return this.cachedSettings
  }

  async updateSettings(settings: Partial<WorkspaceSettings>): Promise<WorkspaceSettings> {
    const updatedSettings = await this.workspaceRepository.updateSettings(settings)
    // A failed save must not replace the cached settings with nothing.
    if (!updatedSettings) {
      throw new Error('Failed to update workspace settings')
    }
    this.fetchSeq += 1
    this.cachedSettings = updatedSettings
    this.settingsLoaded = true
    this.usedFallback = false
    return updatedSettings
  }

  getPlatformBranding(): PlatformBranding {
    if (this.cachedSettings) {
      return {
        platformName: this.cachedSettings.platform_name || 'Sevv',
        platformUrl: this.cachedSettings.platform_url || 'https://app.sevv.com',
        logoUrl: this.cachedSettings.logo_url || '/logo.png',
        primaryColor: this.cachedSettings.urls?.primaryColor || '#389acb'
      }
    }
    return this.workspaceRepository.getPlatformBranding()
  }

  async validateDomain(email: string): Promise<boolean> {
    return this.workspaceRepository.validateDomain(email)
  }

  async validateRegistrationEmail(email: string): Promise<{ isValid: boolean; message: string; companyId?: string; companyName?: string; defaultRole?: string }> {
    // Prefer server-side validation that considers registration mode and company patterns
    if (typeof (this.workspaceRepository as any).validateRegistrationEmail === 'function') {
      return (this.workspaceRepository as any).validateRegistrationEmail(email)
    }
    // Fallback to basic domain validation
    const basic = await this.workspaceRepository.validateDomain(email)
    return { isValid: basic, message: basic ? 'Allowed' : 'Domain blocked' }
  }

  /**
   * Fetch the settings from the server, even when a copy is cached. For callers that must see
   * the current row (the admin's Environment Configuration before it saves). `fresh` also skips
   * cloud's short per-worker cache of the row, which a save on another worker can leave stale.
   */
  async loadSettings(options?: { fresh?: boolean }): Promise<void> {
    const run = this.fetchSettings(options)
    // Callers that join this load (ensureSettingsLoaded) never see a failed fresh read; only the
    // caller that asked for `fresh` does.
    const shared = run.catch(() => undefined)
    this.loadInFlight = shared
    try {
      await run
    } finally {
      if (this.loadInFlight === shared) this.loadInFlight = null
    }
  }

  /**
   * Make sure the settings are in memory without asking the server again when they already are:
   * once per page load, however many controllers mount (AppController, RoomController, the
   * remount after leaving the office). Joins a fetch still running, for example one that
   * outlived a caller's timeout. A fetch that came back empty is tried again, and so is one that
   * threw (the defaults stand in meanwhile), but a thrown one only once per cooldown.
   */
  async ensureSettingsLoaded(): Promise<void> {
    if (this.loadInFlight) return this.loadInFlight
    if (this.usedFallback) {
      if (Date.now() < this.retryFallbackAfter) return
    } else if (this.settingsLoaded && this.cachedSettings) {
      return
    }
    return this.loadSettings()
  }

  private async fetchSettings(options?: { fresh?: boolean }): Promise<void> {
    const seq = ++this.fetchSeq
    try {
      const settings = await this.workspaceRepository.getSettings(options)
      if (seq !== this.fetchSeq) return
      if (!settings && options?.fresh) {
        throw new FreshSettingsUnavailableError()
      }
      if (!settings && this.usedFallback) {
        // Still no answer: keep serving the defaults rather than dropping to null under callers
        // that have been running on them.
        this.retryFallbackAfter = Date.now() + FALLBACK_RETRY_COOLDOWN_MS
        return
      }
      this.cachedSettings = settings
      this.settingsLoaded = true
      this.usedFallback = false
    } catch (error) {
      if (seq !== this.fetchSeq) return
      // A failed fresh read leaves everything the app already loaded (settings, branding) as it
      // is, and tells the caller, which must not act on a copy it could not confirm.
      if (options?.fresh) {
        if (!(error instanceof FreshSettingsUnavailableError)) {
          console.error('Failed to load fresh workspace settings:', error)
        }
        throw error
      }
      console.error('Failed to load workspace settings:', error)
      // Use default settings if loading fails
      this.cachedSettings = this.getDefaultSettings()
      this.settingsLoaded = true
      this.usedFallback = true
      this.retryFallbackAfter = Date.now() + FALLBACK_RETRY_COOLDOWN_MS
    }
  }

  getDefaultSettings(): WorkspaceSettings {
    return {
      id: 'default',
      name: 'Default Workspace',
      platform_name: 'Sevv',
      platform_url: 'https://app.sevv.com',
      logo_url: '/logo.png',
      blocked_domains: [],
      registration_enabled: true,
      urls: {
        primaryColor: '#389acb',
        secondaryColor: '#2c3e50'
      },
      features: {
        mapEditor: true,
        chat: true,
        whiteboard: true
      },
      restrictions: {
        maxRoomsPerUser: 10,
        maxUsersPerRoom: 50
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  }
} 